import { useRef, useState } from "react";
import apiClient, { unwrapData } from "../../services/apiClient";
import {
  getCallLogRecordings,
  getRecordingPlayback,
  getRecordings,
} from "../../services/recordingsApi";
import { normalizeApiError } from "../../utils/errors";

async function getLegacyPlayback(callLogId) {
  const response = await apiClient.get(
    `/api/calls/log/${encodeURIComponent(callLogId)}/recording-url`,
  );
  const data = unwrapData(response) || {};

  return data.url
    ? {
        url: data.url,
        file_name: data.label || `call-${callLogId}-recording`,
      }
    : null;
}

function buildRecordingLabel(call = {}) {
  const labelParts = [call?.caller_name, call?.service_type, call?.call_disposition]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase().replace(/_/g, " "));

  return labelParts.join(" ") || "recording";
}

export default function RecordingButton({
  call,
  className = "",
  compact = false,
  showDownload = true,
  playLabel = "Play",
  hideLabel = "Hide",
  downloadLabel = "Download",
}) {
  const [audioUrl, setAudioUrl] = useState("");
  const [recordingMeta, setRecordingMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [visible, setVisible] = useState(false);
  const audioRef = useRef(null);

  const status = String(call?.recording_status || "").toLowerCase();
  const hasRecording = Boolean(call?.recording_s3_key) || Number(call?.recording_count || 0) > 0;
  const label = buildRecordingLabel(call);

  const resolvePlayback = async () => {
    const callId = call.id;
    const isCanonicalId = Number.isFinite(Number(callId)) && Number(callId) > 0;

    if (isCanonicalId) {
      try {
        const resp = await apiClient.get(`/api/calls/${encodeURIComponent(callId)}/recordings`);
        const data = unwrapData(resp) || {};
        const items = Array.isArray(data.items) ? data.items : [];
        if (items.length > 0) {
          const primary = items[0];
          const playback = await getRecordingPlayback(primary.recording_id || primary.id);
          return { ...playback, id: primary.recording_id || primary.id };
        }
      } catch (_err) {
        // fall through to phone-based lookup
      }

      if (call.phone_number) {
        const { items: phoneItems } = await getRecordings({ phone: call.phone_number, limit: 5 });
        const synced = phoneItems.filter((r) => r.s3_key || r.playback_available);
        if (synced.length > 0) {
          const playback = await getRecordingPlayback(synced[0].id);
          return { ...playback, id: synced[0].id };
        }
      }
    }

    const logRecordings = await getCallLogRecordings(callId);
    if (logRecordings.length > 0) {
      const primaryRecording = logRecordings[0];
      const playback = await getRecordingPlayback(primaryRecording.id);
      return { ...playback, id: primaryRecording.id };
    }

    return getLegacyPlayback(callId);
  };

  const handlePlay = async (event) => {
    event.stopPropagation();

    if (audioUrl) {
      setVisible((current) => !current);
      if (visible && audioRef.current) {
        audioRef.current.pause();
      }
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const playback = await resolvePlayback();

      if (!playback?.url) {
        throw new Error("Recording is not available yet.");
      }

      setAudioUrl(playback.url);
      setRecordingMeta(playback);
      setVisible(true);
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      setErrorMsg(normalizedError.message || "Unable to load recording.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (event) => {
    event.stopPropagation();
    setDownloading(true);
    setErrorMsg("");

    try {
      const playback = recordingMeta?.url ? recordingMeta : await resolvePlayback();

      if (!playback?.url) {
        throw new Error("Recording is not available yet.");
      }

      setRecordingMeta(playback);
      if (!audioUrl) {
        setAudioUrl(playback.url);
      }
      window.open(playback.url, "_blank", "noopener,noreferrer");
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      setErrorMsg(normalizedError.message || "Unable to download recording.");
    } finally {
      setDownloading(false);
    }
  };

  if (!hasRecording) {
    if (status === "failed") {
      return (
        <span className="recording-status recording-status-failed" title="Recording sync failed">
          Sync failed
        </span>
      );
    }

    if (status === "pending") {
      return (
        <span className="recording-status recording-status-pending" title="Recording sync is pending">
          Sync pending
        </span>
      );
    }

    if (status === "disabled" || call?.recording_disabled) {
      return (
        <span className="recording-status" title="Recording disabled for inbound">
          Recording disabled for inbound
        </span>
      );
    }

    return (
      <span className="recording-status" title="Recording unavailable">
        Recording unavailable
      </span>
    );
  }

  return (
    <div
      className={`recording-control ${compact ? "compact" : ""} ${className}`.trim()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="recording-action-row">
        <button
          type="button"
          className="recording-action-btn"
          title={`Play ${label}`}
          aria-label={`Play ${label}`}
          disabled={loading}
          onClick={handlePlay}
        >
          {loading ? "Loading..." : visible ? hideLabel : playLabel}
        </button>
        {showDownload ? (
          <button
            type="button"
            className="recording-action-btn secondary"
            title={`Download ${label}`}
            aria-label={`Download ${label}`}
            disabled={downloading}
            onClick={handleDownload}
          >
            {downloading ? "Opening..." : downloadLabel}
          </button>
        ) : null}
      </div>
      {audioUrl && visible ? (
        <audio
          ref={audioRef}
          controls
          preload="metadata"
          src={audioUrl}
          className="recording-audio-player"
        />
      ) : null}
      {errorMsg ? <span className="recording-error-text">{errorMsg}</span> : null}
    </div>
  );
}
